import { Location, SectionFragment } from 'generated/graphql';
import technicalStopoverResolver from './technicalStopover';
import calculateMinutesDuration from 'clientState/calculateMinutesDuration';

const extendLocation = (
  location: LocationWithType,
  terminal?: string | null
) => ({
  ...location,
  ...(terminal ? { terminal } : undefined),
  id: !terminal ? location.id : location.id + '_' + terminal,
});
type LocationType = 'absolute' | 'connection';
type LocationWithType = Location & { type: LocationType };
const addLocationType = (
  location: Location,
  type: LocationType
): LocationWithType => ({
  ...location,
  type,
});

const createTrip = (
  section: SectionFragment,
  {
    departure,
    destination,
    departureDate,
    arrivalDate,
    duration,
    id,
  }: {
    id: string;
    departure: ReturnType<typeof extendLocation>;
    destination: LocationWithType;
    departureDate: string;
    arrivalDate: string;
    duration?: number | null;
  }
) => ({
  ...section,
  id,
  departure,
  destination,
  departureDate,
  arrivalDate,
  duration: duration || calculateMinutesDuration(arrivalDate, departureDate),
});

export default function sectionResolver(
  section: SectionFragment,
  isLastInSegment: boolean,
  isFirstSegment: boolean
): SectionFragment & {
  trips: ReturnType<typeof createTrip>[];
  technicalStops: ReturnType<typeof technicalStopoverResolver>[];
} {
  const technicalStops: ReturnType<typeof technicalStopoverResolver>[] = [];
  const trips: ReturnType<typeof createTrip>[] = [];
  let departureLocation: ReturnType<typeof extendLocation> = extendLocation(
    addLocationType(
      section.departure,
      isFirstSegment ? 'absolute' : 'connection'
    ),
    section.departureTerminal
  );
  let tripDepartureDate = section.departureDate;
  section.technicalStops?.forEach((technicalStop, index) => {
    trips.push(
      createTrip(section, {
        id: `${section.id}_${index}`,
        departure: departureLocation,
        departureDate: tripDepartureDate,
        destination: extendLocation(
          addLocationType(technicalStop.location, 'connection')
        ),
        arrivalDate: technicalStop.arrivalDate,
      })
    );
    technicalStops.push(technicalStopoverResolver(technicalStop));
    departureLocation = extendLocation(
      addLocationType(technicalStop.location, 'connection')
    );
    tripDepartureDate = technicalStop.departureDate;
  });

  // if there are some technicalStops, the duration from frontend-api returns us the full length of section
  // but we only want to show the duration from last step, and the value is not exact
  // we must calculate with arrivalDate - departureDate

  const duration = section.technicalStops?.length ? null : section.duration;

  trips.push(
    createTrip(section, {
      id: `${section.id}_${technicalStops.length}`,
      departure: departureLocation,
      destination: extendLocation(
        addLocationType(
          section.destination,
          isLastInSegment ? 'absolute' : 'connection'
        ),
        section.arrivalTerminal
      ),
      departureDate: tripDepartureDate,
      arrivalDate: section.arrivalDate,
      duration,
    })
  );

  return {
    ...section,
    trips,
    technicalStops,
  };
}
